import type { FC, KeyboardEvent } from 'react'
import type { PlaygroundPlatform } from '@/constants/home'

import { useRef, useState } from 'react'
import { Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Photo } from '@/components/kit/photo'
import { PlatformIcon } from '@/components/kit/platform-icon'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { playground } from '@/constants/home'

/* Background accent shapes change with each platform */
const Shapes: FC<{ shape: PlaygroundPlatform['shape'] }> = ({ shape }) => {
    return (
        <div
            aria-hidden='true'
            className='pointer-events-none absolute inset-0 overflow-hidden'
        >
            {shape === 'circles' && (
                <>
                    <span className='pop-up absolute -right-16 -top-16 size-72 rounded-full border-[18px] border-ember/80' />
                    <span className='pop-up absolute -bottom-10 left-[8%] size-40 rounded-[2.5rem] bg-butter [animation-delay:120ms]' />
                    <span className='pop-up absolute right-[30%] top-[64%] size-16 rounded-full bg-ink [animation-delay:220ms]' />
                </>
            )}
            {shape === 'zigzag' && (
                <svg
                    viewBox='0 0 600 400'
                    preserveAspectRatio='none'
                    className='pop-up absolute inset-0 h-full w-full'
                >
                    <path d='M-20 320 L60 250 L140 330 L220 240 L300 320 L380 230 L460 320 L540 220 L620 300' fill='none' stroke='#ff5c00' strokeWidth='22' strokeLinejoin='round' />
                    <path d='M-20 120 L80 60 L180 130 L280 50' fill='none' stroke='#1e1e1e' strokeWidth='10' strokeLinejoin='round' />
                </svg>
            )}
            {shape === 'frames' && (
                <>
                    <span className='pop-up absolute -right-10 top-8 aspect-video w-72 rotate-6 rounded-2xl border-[10px] border-ember' />
                    <span className='pop-up absolute -left-12 bottom-10 aspect-video w-56 -rotate-6 rounded-2xl bg-ink [animation-delay:150ms]' />
                    <span className='pop-up absolute bottom-16 right-[26%] aspect-video w-24 rounded-lg bg-butter [animation-delay:250ms]' />
                </>
            )}
            {shape === 'grid' && (
                <div className='pop-up absolute inset-0 bg-[linear-gradient(to_right,rgb(30_30_30/0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgb(30_30_30/0.08)_1px,transparent_1px)] [background-size:44px_44px]'>
                    <span className='absolute right-10 top-10 h-24 w-44 bg-ember' />
                    <span className='absolute bottom-12 left-[12%] h-10 w-60 bg-ink' />
                </div>
            )}
        </div>
    )
}

/* A sample post in the shape each platform rewards */
const Sample: FC<{ platform: PlaygroundPlatform }> = ({ platform }) => {
    if (platform.key === 'instagram') {
        return (
            <div className='w-[280px] overflow-hidden rounded-[1.5rem] border border-ink/10 bg-paper shadow-[0_30px_60px_-36px_rgb(30_30_30/0.6)]'>
                <div className='relative aspect-[4/5] bg-ember p-6'>
                    <p className='font-mono text-[10px] uppercase tracking-[0.16em] text-ink/70'>Slide 1 of 10</p>
                    <p className='mt-4 font-heading text-[2rem] font-bold leading-[0.95] tracking-[-0.04em] text-ink'>
                        How to fix a pulled thread in two minutes
                    </p>
                    <p className='absolute bottom-6 left-6 font-mono text-xs text-ink'>Swipe for step one</p>
                </div>
                <div className='flex justify-center gap-1.5 py-3'>
                    {Array.from({ length: 10 }, (_, index) => (
                        <span
                            key={index}
                            className={cn('size-1.5 rounded-full', index === 0 ? 'bg-ember' : 'bg-ink/15')}
                        />
                    ))}
                </div>
            </div>
        )
    }

    if (platform.key === 'tiktok') {
        return (
            <div className='relative aspect-[9/16] w-[230px] overflow-hidden rounded-[1.75rem] border-[6px] border-ink bg-ink shadow-[0_30px_60px_-30px_rgb(30_30_30/0.7)]'>
                <Photo
                    src='/images/hero/selfie.jpg'
                    alt=''
                    sizes='230px'
                    position='50% 30%'
                    className='absolute inset-0'
                />
                <span className='absolute inset-0 bg-black/15' />
                <p className='absolute inset-x-4 top-[38%] text-center font-heading text-2xl font-bold leading-[0.95] tracking-[-0.03em] text-white'>
                    i was today years old when
                </p>
                <p className='absolute bottom-4 left-4 right-14 text-[11px] leading-snug text-white'>
                    <span className='font-semibold'>@sundayskin</span> part 3 of things nobody tells you about SPF
                </p>
                <span className='absolute bottom-4 right-3 font-mono text-[10px] text-white'>0:21</span>
            </div>
        )
    }

    if (platform.key === 'youtube') {
        return (
            <div className='w-[320px]'>
                <div className='relative aspect-video overflow-hidden rounded-2xl shadow-[0_30px_60px_-36px_rgb(30_30_30/0.6)]'>
                    <Photo
                        src='/images/hero/street.jpg'
                        alt=''
                        sizes='320px'
                        position='50% 35%'
                        className='absolute inset-0'
                    />
                    <p className='absolute bottom-3 left-3 max-w-[60%] bg-butter px-2 py-1 font-heading text-xl font-bold leading-none tracking-[-0.03em] text-ink'>
                        I wore these for a year
                    </p>
                    <span className='absolute bottom-3 right-3 rounded bg-ink/85 px-1.5 py-0.5 font-mono text-[11px] text-cream'>12:48</span>
                </div>
                <p className='mt-3 text-[15px] font-semibold leading-snug'>Are expensive sunglasses worth it? An honest year-long test</p>
                <p className='mt-1 font-mono text-[11px] text-stone'>148K views · 3 weeks ago</p>
            </div>
        )
    }

    return (
        <div className='w-[320px] rounded-2xl border border-ink/10 bg-paper p-5 shadow-[0_30px_60px_-36px_rgb(30_30_30/0.5)]'>
            <div className='flex items-center gap-3'>
                <span className='flex size-10 items-center justify-center rounded-full bg-ink font-heading text-sm font-bold text-cream'>MO</span>
                <div>
                    <p className='text-sm font-semibold'>Maya Okafor</p>
                    <p className='font-mono text-[10px] text-stone'>Founder, Updraft · 1d</p>
                </div>
            </div>
            <p className='mt-4 text-[14px] leading-relaxed'>
                We lost a pitch last week. The client said our work was too honest. Here are the four slides we would show them again, unchanged.
            </p>
            <div className='mt-4 grid grid-cols-4 gap-1.5'>
                {['bg-ember', 'bg-ink', 'bg-butter', 'bg-sand'].map(tone => (
                    <span
                        key={tone}
                        className={cn('aspect-[4/5] rounded-md', tone)}
                    />
                ))}
            </div>
            <p className='mt-3 font-mono text-[11px] text-stone'>612 reactions · 94 comments</p>
        </div>
    )
}

export const PlatformPlayground: FC = () => {
    const [current, setCurrent] = useState(0)
    const tabs = useRef<Array<HTMLButtonElement | null>>([])
    const platform = playground[current]

    const onKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return

        event.preventDefault()

        const next = (index + (event.key === 'ArrowRight' ? 1 : -1) + playground.length) % playground.length

        setCurrent(next)
        tabs.current[next]?.focus()
    }

    return (
        <section
            id='platform-playground'
            aria-labelledby='playground-title'
            className='py-20 md:py-28'
        >
            <div className='mx-auto max-w-[1400px] px-5 md:px-10'>
                <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-7'>
                        <SectionLabel index='05'>Platform playground</SectionLabel>
                        <SplitText
                            as='h2'
                            id='playground-title'
                            lines={['Every app has', 'its own weather']}
                            italic='weather'
                            className='mt-5 text-[clamp(2.75rem,5.6vw,5.25rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                        />
                    </div>
                    <Reveal
                        as='p'
                        delay={150}
                        className='text-lg leading-relaxed text-stone lg:col-span-5'
                    >
                        What works on one platform falls flat on the next. Pick an app and see the format we would lead with, plus our short list of what works there.
                    </Reveal>
                </div>

                <div
                    role='tablist'
                    aria-label='Platforms'
                    className='mt-12 flex flex-wrap gap-2'
                >
                    {playground.map((item, index) => (
                        <button
                            key={item.key}
                            ref={element => {
                                tabs.current[index] = element
                            }}
                            type='button'
                            role='tab'
                            id={`tab-${item.key}`}
                            aria-selected={current === index}
                            aria-controls={`panel-${item.key}`}
                            tabIndex={current === index ? 0 : -1}
                            onClick={() => setCurrent(index)}
                            onKeyDown={event => onKey(event, index)}
                            className={cn(
                                'inline-flex items-center gap-2 rounded-full border px-5 py-3 text-sm font-medium transition-colors duration-300',
                                current === index ? 'border-ink bg-ink text-cream' : 'border-ink/15 bg-paper/60 text-ink hover:border-ink/40'
                            )}
                        >
                            <PlatformIcon
                                name={item.key}
                                className='size-4'
                            />
                            {item.label}
                        </button>
                    ))}
                </div>

                <div
                    role='tabpanel'
                    id={`panel-${platform.key}`}
                    aria-labelledby={`tab-${platform.key}`}
                    className='relative mt-6 grid overflow-hidden rounded-[2rem] border border-ink/10 bg-paper md:rounded-[2.5rem] lg:grid-cols-2'
                >
                    <div
                        key={`${platform.key}-sample`}
                        className='relative flex min-h-[460px] items-center justify-center overflow-hidden bg-sand p-8'
                    >
                        <Shapes shape={platform.shape} />
                        <div className='pop-up relative'>
                            <Sample platform={platform} />
                        </div>
                    </div>

                    <div
                        key={`${platform.key}-notes`}
                        className='flex flex-col p-7 md:p-10'
                    >
                        <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-ember'>Lead format · {platform.format}</p>
                        <p className='pop-up mt-4 font-heading text-[clamp(1.6rem,2.6vw,2.4rem)] font-bold leading-[1.08] tracking-[-0.035em]'>
                            {platform.note}
                        </p>

                        <p className='mt-8 font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>What works here</p>
                        <ul className='mt-3 flex flex-col gap-2'>
                            {platform.works.map((item, index) => (
                                <li
                                    key={item}
                                    className='pop-up flex items-start gap-3 text-base'
                                    style={{ animationDelay: `${120 + index * 90}ms` }}
                                >
                                    <span className='mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-ember text-ink'>
                                        <Check className='size-3' />
                                    </span>
                                    {item}
                                </li>
                            ))}
                            <li className='pop-up flex items-start gap-3 text-base text-stone [animation-delay:420ms]'>
                                <span className='mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-ink/10 text-ink'>
                                    <X className='size-3' />
                                </span>
                                Skip: {platform.skip}
                            </li>
                        </ul>

                        <div className='mt-auto flex items-end gap-4 border-t border-ink/10 pt-6'>
                            <p className='font-heading text-5xl font-bold leading-none tracking-[-0.045em]'>{platform.stat.value}</p>
                            <p className='max-w-[16rem] text-sm leading-snug text-stone'>{platform.stat.label}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
